/**
 * Mobile Thumbnails Component
 * Handles independent thumbnail navigation for mobile devices
 */

class MobileThumbnails {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.thumbnails = container.querySelectorAll('.mobile-thumbnails__thumbnail');
    this.mediaGallery = container.closest('media-gallery');
    this.slideshow = this.mediaGallery?.querySelector('slideshow-component');
    
    this.init();
  }

  init() {
    if (!this.thumbnails.length || !this.mediaGallery) return;
    
    this.bindEvents();
    this.observeMediaChanges();
  }

  bindEvents() {
    this.thumbnails.forEach((/** @type {HTMLElement} */ thumbnail, /** @type {number} */ index) => {
      thumbnail.addEventListener('click', () => {
        this.selectThumbnail(index);
        this.navigateToMedia(index);
      });
    });
  }

  /**
   * @param {number} index
   */
  selectThumbnail(index) {
    // Update thumbnail selection state
    this.thumbnails.forEach((/** @type {HTMLElement} */ thumb, /** @type {number} */ i) => {
      thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  /**
   * @param {number} index
   */
  navigateToMedia(index) {
    // Navigate slideshow if it exists
    if (this.slideshow && typeof /** @type {any} */ (this.slideshow).select === 'function') {
      /** @type {any} */ (this.slideshow).select(index);
      return;
    }

    // For grid layout, scroll to the specific media item
    const mediaItems = this.mediaGallery?.querySelectorAll('[ref="media[]"]');
    const targetMedia = mediaItems?.[index];
    
    if (targetMedia && targetMedia instanceof HTMLElement) {
      targetMedia.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  observeMediaChanges() {
    // Observe changes in the main gallery to sync thumbnail selection
    if (!this.slideshow) return;

    // Listen for slideshow changes
    const observer = new MutationObserver(() => {
      const current = /** @type {any} */ (this.slideshow)?.current;
      if (current !== undefined) {
        this.selectThumbnail(current);
      }
    });

    observer.observe(this.slideshow, {
      attributes: true,
      attributeFilter: ['data-current']
    });

    // Also listen for custom events if the slideshow dispatches them
    this.slideshow.addEventListener('slidechange', (/** @type {CustomEvent} */ event) => {
      if (event.detail && event.detail.current !== undefined) {
        this.selectThumbnail(event.detail.current);
      }
    });
  }
}

// Initialize mobile thumbnails when DOM is ready
function initMobileThumbnails() {
  const mobileThumbnailContainers = document.querySelectorAll('.mobile-thumbnails');
  
  mobileThumbnailContainers.forEach(container => {
    if (container instanceof HTMLElement) {
      new MobileThumbnails(container);
    }
  });
}

// Initialize on DOM content loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileThumbnails);
} else {
  initMobileThumbnails();
}

// Re-initialize when new content is loaded (for AJAX/dynamic content)
document.addEventListener('shopify:section:load', initMobileThumbnails);

export default MobileThumbnails;